import { Injectable, NgZone, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, first, tap } from 'rxjs';
import { BusinessCentralSyncCompletedEvent } from '../models/business-central/business-central-sync-completed-event.interface';
import { BusinessCentralSyncHub } from '../signalr-hubs/business-central-sync.hub';
import { AuthService } from './auth.service';
import { BusinessCentralSyncNotificationService } from './business-central-sync-notification.service';
import { ToastService } from './toast.service';

/**
 * Root-singleton listener for Business Central sync completion events. It owns the
 * SINGLE app-lifetime subscription to businessCentralSyncCompleted$, so exactly one
 * toast is shown per event regardless of how many <app-navbar-profile-info> instances
 * are mounted. On each event it also refetches the persisted notifications and merges
 * them into AuthorizedUserData, mirroring the assignment-change fetch-and-merge flow.
 */
@Injectable({
  providedIn: 'root',
})
export class BusinessCentralSyncListenerService {
  private readonly businessCentralSyncHub = inject(BusinessCentralSyncHub);
  private readonly notificationService = inject(
    BusinessCentralSyncNotificationService,
  );
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly ngZone = inject(NgZone);

  private isInitialized = false;

  public init(accessToken: string): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    this.businessCentralSyncHub.restartConnection(accessToken);

    this.businessCentralSyncHub.businessCentralSyncCompleted$
      .pipe(
        // SignalR server-to-client handlers fire OUTSIDE Angular's NgZone, so
        // mutating ToastService.toasts directly would not trigger change
        // detection until the next zone-triggering event. Re-enter the zone so
        // the toast renders immediately.
        tap((event) =>
          this.ngZone.run(() => {
            this.showToast(event);
            this.loadAndMergeNotifications();
          }),
        ),
      )
      .subscribe();

    // Seed the notifications panel on init with persisted notifications so it is
    // populated immediately, instead of waiting for the first live event.
    this.loadAndMergeNotifications();
  }

  private showToast(event: BusinessCentralSyncCompletedEvent): void {
    const operationLabel = this.translateService.instant(
      this.resolveOperationLabelKey(event.operation),
    );

    if (event.success) {
      this.toastService.show({
        text: this.translateService.instant('COMPONENTS.BC_SYNC.TOAST_SUCCESS', {
          operation: operationLabel,
        }),
        className: 'bg-success text-light',
      });
      return;
    }

    this.toastService.show({
      text: this.translateService.instant('COMPONENTS.BC_SYNC.TOAST_FAILURE', {
        operation: operationLabel,
        message: event.errorMessage ?? '',
      }),
      className: 'bg-danger text-light',
    });
  }

  private resolveOperationLabelKey(operation: string): string {
    if (operation === 'sync-items') {
      return 'COMPONENTS.BC_SYNC.OPERATIONS.SYNC_ITEMS';
    }
    if (operation === 'sync-units-of-measure') {
      return 'COMPONENTS.BC_SYNC.OPERATIONS.SYNC_UNITS_OF_MEASURE';
    }
    if (operation === 'sync-goods') {
      return 'COMPONENTS.BC_SYNC.OPERATIONS.SYNC_GOODS';
    }
    return operation;
  }

  private loadAndMergeNotifications(): void {
    this.notificationService
      .getNotifications()
      .pipe(
        first(),
        tap((businessCentralSyncNotifications) => {
          const userData = this.authService.user$.getValue().data;
          if (!userData) {
            return;
          }

          this.authService.updateUserData({
            ...userData,
            businessCentralSyncNotifications,
          });
        }),
        catchError((error: unknown) => {
          console.error('Failed to refresh BC sync notifications', error);
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
